import { useState } from "react";
import { useLazyGetRoleManagementUserLoginQuery } from "../services/roleManagementApi";
import { IUser } from "@pulse-ui/types/components/RoleManagement";

const EMPTY_USER: IUser = {
  org_user_id: "",
  firstname: "",
  lastname: "",
  is_active: true,
  created: "",
  last_modified: "",
  roles: [],
  portfolios: [],
  email: "",
};

export const useUserDetailsModal = () => {
  const [open, setOpen] = useState(false);
  const [userDetails, setUserDetails] = useState<IUser>(EMPTY_USER);
  const [clipboardSnackbar, setClipboardSnackbar] = useState<{
    open: boolean;
    success: boolean | null;
    message: string;
  }>({ open: false, success: null, message: "" });

  const [getUserLogin] = useLazyGetRoleManagementUserLoginQuery();

  const openModal = async (user: IUser) => {
    const loginRes = await getUserLogin(user.org_user_id);
    setUserDetails({ ...user, email: loginRes.data?.email ?? "" });
    setOpen(true);
  };

  const closeUserDetailsModal = () => {
    setUserDetails(EMPTY_USER);
    setOpen(false);
  };

  const copyToClipboard = async (text: string, name: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(text);
      setClipboardSnackbar({
        open: true,
        success: true,
        message: `${name} copied to clipboard.`,
      });
    } catch {
      setClipboardSnackbar({
        open: true,
        success: false,
        message: `Failed to copy ${name} to clipboard.`,
      });
    }
  };

  const handleCloseSnackbar = () => {
    setClipboardSnackbar((prev) => ({ ...prev, open: false }));
  };

  const syncRoles = (orgUserId: string, roles: string[]) => {
    setUserDetails((prev) =>
      prev.org_user_id === orgUserId ? { ...prev, roles } : prev,
    );
  };

  const syncPortfolios = (orgUserId: string, portfolios: string[]) => {
    setUserDetails((prev) =>
      prev.org_user_id === orgUserId ? { ...prev, portfolios } : prev,
    );
  };

  return {
    open,
    userDetails,
    clipboardSnackbar,
    openModal,
    closeUserDetailsModal,
    copyToClipboard,
    handleCloseSnackbar,
    syncRoles,
    syncPortfolios,
  };
};
